<template>
  <v-card
  flat
  height="100%"
  tile
  id="playlistAttachmentPickerCard"
  tabindex="-1"
  ref="addAttachmentCard"
  :ripple="false">
    <v-list
    :color="playlistColor"
    height="65"
    dark
    tile>
      <v-list-item :color="playlistColor">
        <v-list-item-avatar>
          <v-btn icon @click="closeSlideout" aria-label="Close add attachment tab">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>Add resource to {{ selectedModuleName }}.<v-icon class="ml-2">mdi-file-link-outline</v-icon></v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <v-container>
      <v-card
        flat
        class="mt-2">
        <v-card-title class="playlist-headline font-weight-light black--text">
          Add Attachment
        </v-card-title>
        <v-card-text>
          <PlaylistResources v-if="moduleSelected" :module="moduleSelected"></PlaylistResources>
        </v-card-text>
      </v-card>
      <v-card
        flat
        class="mt-2">
        <v-card-title class="playlist-headline font-weight-light black--text">
          Add Web Embed
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="resourceURL"
            label="Insert website URL"
            prepend-icon="mdi-link"
            @blur="handleAddLinkBlur"
            :error-messages="resourceURLErrors"
          ></v-text-field>
          <p v-show="!resourceURLHasErrors && $v.resourceURL.$dirty">No preview below? Some sites block this functionality, as well as insecure http:// websites.</p>
          <iframe v-show="!resourceURLHasErrors && $v.resourceURL.$dirty" class="previewIframe" :src="resourceURL"></iframe>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :color="playlistColor"
            class="white--text"
            rounded
            min-width="100"
            :disabled="(
              resourceURLHasErrors
              || isUpdating
              || !$v.resourceURL.$dirty
            )"
            @click="handleAddLink"
            ref="addLinkButton"
            aria-label="Add Link"
          >
              Add Link
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card
        flat
        class="mt-2">
        <v-card-title class="playlist-headline font-weight-light black--text">
          Add Text Item
        </v-card-title>
        <v-card-text style="height: 311px">
          <v-text-field
            v-model.trim="textTitle"
            label="Text title"
            prepend-icon="mdi-text-short"
            :error-messages="textTitleErrors"
            @input="$v.textTitle.$touch"
            :color="playlistColor"
          ></v-text-field>
          <TinyMCE
            v-if="playlistAttachmentPickerOpened"
            :apiEnabled="true"
            aria-label="Playlist rich text editor"
            :token="userToken"
            mode="text-editor"
            :tinymceKey="moduleSelected.id"
            :footerKey="playlist.id"
            placeholder="Hello, World!"
            service="playlist"
            :brandingColor="playlistColor"
            submissionButtonText="Add Text"
            :submitFunction="handleAddText"
          />
        </v-card-text>
      </v-card>
    </v-container>
  </v-card>
</template>

<script>

import {
  mapState,
  mapActions,
  mapMutations,
} from 'vuex';

import _ from 'lodash';
import {
  maxLength,
  required,
  url,
} from 'vuelidate/lib/validators';

import PlaylistResources from '../PlaylistResources.vue';

export default {
  name: 'PlaylistAttachmentPicker',
  props: [
    'open',
  ],
  components: {
    PlaylistResources,
  },
  data: () => ({
    baseURL: null,
    playlistAttachmentPickerOpened: false,
    resourceURL: 'https://',
    textTitle: '',
  }),
  computed: {
    ...mapState([
      'isUpdating',
      'moduleSelected',
      'playlist',
      'slideoutData',
      'userToken',
    ]),
    playlistColor() {
      return _.get(this.playlist, 'default_color', '#2196F3');
    },
    resourceURLHasErrors() {
      return this.$v.resourceURL.$anyError;
    },
    resourceURLErrors() {
      const errors = [];
      if (!this.$v.resourceURL.$dirty) return errors;
      if (!this.$v.resourceURL.required) {
        errors.push('Resource URL is required.');
      }
      if (!this.$v.resourceURL.url) {
        if (!this.resourceURL.includes('http')) errors.push('URL must start with http:// or https://');
        else errors.push('Must be a valid URL.');
      }
      return errors;
    },
    selectedModuleName() {
      return _.get(this.moduleSelected, 'name', 'Playlist');
    },
    textTitleErrors() {
      const errors = [];
      if (!this.$v.textTitle.$dirty) return errors;
      if (!this.$v.textTitle.required) errors.push('A title is required.');
      if (!this.$v.textTitle.maxLength) errors.push('The max length is 255 characters.');
      return errors;
    },
  },
  methods: {
    ...mapActions([
      'addLink',
    ]),
    ...mapMutations([
      'setIsUpdating',
      'setModuleSelected',
      'setPlaylist',
      'setSlideout',
      'toggleSnackbar',
    ]),
    closeSlideout() {
      this.resourceURL = 'https://';
      this.setModuleSelected(null);
      this.setSlideout(null);
    },
    handleAddLink() {
      this.addLink({
        url: this.resourceURL,
        order: this.moduleSelected.moduleItems.length,
      });
      this.resourceURL = 'https://';
    },
    handleAddLinkBlur() {
      this.$v.resourceURL.$touch();
      if (!this.$v.resourceURL.$error) {
        this.$nextTick(() => this.$refs.addLinkButton.$el.focus());
      }
    },
    async handleAddText(value) {
      try {
        this.setIsUpdating(true);
        this.setSlideout(null);
        const { data: { playlist: updatedPlaylist } } = await this.$api.module.addText({
          playlistId: this.playlist.id,
          moduleId: this.moduleSelected.id,
          text: {
            title: this.textTitle,
            body: value,
            order: this.moduleSelected.moduleItems.length,
          },
        });
        this.setPlaylist(updatedPlaylist);
        this.textTitle = '';
      } catch(e) {
        this.toggleSnackbar({
          text: 'Error adding text.',
          color: 'error',
        });
      } finally {
        this.$v.textTitle.$reset();
        this.setModuleSelected(null);
        this.setIsUpdating(false);
      }
    },
    setLinkInputColor() {
      let [icon] = document.getElementsByClassName('mdi-link');
      if (icon) icon.style.color = this.playlist.default_color;
      [icon] = document.getElementsByClassName('mdi-text-short');
      if (icon) icon.style.color = this.playlist.default_color;
      [icon] = document.getElementsByClassName('mdi-card-text-outline');
      if (icon) icon.style.color = this.playlist.default_color;
    },
  },
  validations: {
    resourceURL: {
      required,
      url,
    },
    textTitle: {
      maxLength: maxLength(255),
      required,
    },
  },
  async mounted() {
    this.baseURL = this.$getServerUrl();
    this.setLinkInputColor();
  },
  watch: {
    'playlist.default_color': function defaultColorWatcher(n) {
      if (n) this.setLinkInputColor();
    },
    resourceURL(n, o) {
      if (
        (
          n.length === 0
          && o.length > 0
        )
        || (
          n.length
          && (
            n === 'http://'
            || n === 'https://'
          )
        )
      ) this.$v.resourceURL.$reset();
    },
    async slideoutData(newValue, oldValue) { // eslint-disable-line
      if (
        newValue
        && newValue?.type === 'playlistAttachmentPicker'
      ) {
        this.playlistAttachmentPickerOpened = true;
        await this.$nextTick();
        setTimeout(() => {
          this.$refs.addAttachmentCard.$el.focus();
        }, 100);
      } else {
        this.playlistAttachmentPickerOpened = false;
      }
    },
  },
};
</script>

<style scoped>
  #playlistBulbAttachmentCard {
    overflow: hidden;
  }
  .aria-feedback {
    text-indent: -10000px;
    font-size: 0.1px;
  }
  .previewIframe {
    width: 100%;
    height: 250px;
    border: 0;
  }
</style>
